import {
  aiAnalyzeSpot,
  aiAnalyzeExternalLink,
  aiGenerateRouteMetadata,
  aiGenerateSpotName,
  aiGenerateSpotShareSummary,
  aiGenerateTrailName,
  aiClassifyWaypoints,
  aiClassifyRouteSuitability
} from "./firebaseService";
import type { WaypointIcon, VehicleCategory, TerrainPreference } from '../types';

const analysisCache = new Map<string, any>();

export const analyzeSpot = async (
  spotData: any,
  isReanalysis: boolean = false,
  retryCount = 0,
  signal?: AbortSignal,
  isStrictValidation: boolean = false
): Promise<any> => {
  const MAX_RETRIES = 0;

  if (!isReanalysis && !isStrictValidation && analysisCache.has(spotData.id)) {
    const cached = analysisCache.get(spotData.id);
    if (cached) return cached;
  }

  try {
    if (signal?.aborted) throw new Error("AbortError");
    const response = await aiAnalyzeSpot(spotData, isReanalysis, isStrictValidation);
    if (!response?.ok || !response.data) return null;
    const finalResult = response.data;
    analysisCache.set(spotData.id, finalResult);
    return finalResult;
  } catch (error: any) {
    if (error?.name === 'AbortError' || error?.message === 'AbortError') return null;
    if (retryCount < MAX_RETRIES) return analyzeSpot(spotData, isReanalysis, retryCount + 1, signal, isStrictValidation);
    return null;
  }
};

export const generateRouteMetadata = async (
  startCoord: [number, number],
  endCoord: [number, number],
  distanceKm: number,
  waypoints?: [number, number][],
  elevationProfile?: { distance: number; elevation: number }[],
  waypointNames?: string[]
): Promise<{
  title: string;
  description: string;
  startPlace?: string;
  endPlace?: string;
  bestSeasons?: string[];
  winterSuitability?: string;
  seasonalNote?: string;
} | null> => {
  try {
    const response = await aiGenerateRouteMetadata(startCoord, endCoord, distanceKm, waypoints, elevationProfile, waypointNames);
    const rd = response.data as any;
    return {
      title: String(rd.title),
      description: String(rd.description),
      startPlace: rd.startPlace ? String(rd.startPlace) : undefined,
      endPlace: rd.endPlace ? String(rd.endPlace) : undefined,
      bestSeasons: (Array.isArray(rd.bestSeasons) ? rd.bestSeasons : undefined) as ('spring' | 'summer' | 'autumn' | 'winter')[] | undefined,
      winterSuitability: rd.winterSuitability ? String(rd.winterSuitability) : undefined,
      seasonalNote: rd.seasonalNote ? String(rd.seasonalNote) : undefined,
    };
  } catch {
    return null;
  }
};

export const analyzeExternalLink = async (url: string): Promise<{ title: string, type: 'article' | 'video' | 'image' | 'other' } | null> => {
  try {
    const response = await aiAnalyzeExternalLink(url);
    const type = String(response?.data?.type || 'other');
    const safeType = (['article', 'video', 'image', 'other'].includes(type) ? type : 'other') as 'article' | 'video' | 'image' | 'other';
    if (!response?.ok || !response?.data?.title) return null;
    return { title: String(response.data.title), type: safeType };
  } catch {
    return null;
  }
};

export const generateTrailName = async (input: {
  spotName?: string;
  municipality?: string;
  county?: string;
  lengthKm?: number;
  difficulty?: string;
  trailType?: string;
}): Promise<string | null> => {
  try {
    const response = await aiGenerateTrailName(input);
    const raw = String(response?.data?.name || '').trim();
    if (!response?.ok || !raw) return null;
    return raw.replace(/\s+/g, ' ').slice(0, 60);
  } catch {
    return null;
  }
};

export const generateSpotShareSummary = async (input: {
  spotName?: string;
  highway?: string;
  surfaceType?: string;
  nearestHouse?: number;
  score?: number;
  features?: string[];
  municipality?: string;
  county?: string;
}): Promise<string | null> => {
  try {
    const response = await aiGenerateSpotShareSummary(input);
    const raw = String(response?.data?.summary || '').trim();
    if (!response?.ok || !raw) return null;
    return raw.replace(/\s+/g, ' ').slice(0, 320);
  } catch {
    return null;
  }
};

export const generateSpotName = async (input: {
  address?: string;
  municipality?: string;
  county?: string;
  highway?: string;
  surfaceType?: string;
  features?: string[];
}): Promise<string | null> => {
  try {
    const response = await aiGenerateSpotName(input);
    const raw = String(response?.data?.name || '').trim();
    if (!response?.ok || !raw) return null;
    return raw.replace(/\s+/g, ' ').slice(0, 48);
  } catch {
    return null;
  }
};

const VALID_ICONS: WaypointIcon[] = ['pin', 'camp', 'parking', 'water', 'viewpoint', 'danger', 'food', 'star'];

/** Keyword-based waypoint icon classification — fallback when AI is unavailable.
 *  Covers Norwegian, Swedish, and English with common inflected forms. */
const heuristicWaypointIcon = (name: string, description?: string): WaypointIcon => {
  const txt = `${name} ${description || ''}`.toLowerCase();

  // Parking / trailhead / rest area
  if (/parker(?:ing|ingsplats)?|p[\s-]?plass|car\s?park|trailhead|startpunkt|rasteplats|rasteplass|rest\s?area|rastplats|avkjørsel|infart/i.test(txt)) return 'parking';

  // Camp / shelter / cabin (NO: hytte/koie, SE: stuga/stugan/vindskydd)
  if (/telt(?:plass)?|leir|hytt[ea]n?|koie[nr]?|gapahuk|cabin|hut|shelter|camp(?:ing)?|overnatting|bivuakk|dnt|stug(?:a|an|or)|vindskydd|övernattning|raststuga|fjällstation|fjellstue|seter|støl/i.test(txt)) return 'camp';

  // Water features (NO: vann/vatn/foss, SE: sjö/sjön/å/älv/bäck)
  if (/vann(?:et)?|vatn|bekk(?:en)?|elv(?:a|en)?|foss(?:en)?|kilde|spring|water|drinking|innsjø|tjern|brønn|sjö[en]?|å[en]?(?:\b)|älv(?:en)?|bäck(?:en)?|göl|damm(?:en)?|källa[rn]?|vattenfall/i.test(txt)) return 'water';

  // Viewpoint / summit (NO: topp/tind/varde, SE: topp/berg/utsiktsplats)
  if (/utsikt(?:s(?:punkt|plats|torn))?|topp(?:en)?|tind(?:en)?|fjell(?:et)?|peak|summit|viewpoint|panorama|varde[nr]?|høgste|eggen|nutane?|piggen?|berg(?:et)?|klint|ås(?:en)?|kullen?|utkiksplats/i.test(txt)) return 'viewpoint';

  // Danger / crossing (NO: bro/kryss, SE: bron/korsning)
  if (/fare|danger|steinras|varsling|caution|bro[en]?n?|bridge|kryss(?:ning)?|crossing|varning|lavine|ras|stenras|korsning/i.test(txt)) return 'danger';

  // Food / dining (NO: kafé/kiosk, SE: fik/konditori/värdshus)
  if (/mat(?:butik|affär)?|kafé|kafe|restaurant|kiosk|butik[ken]?|spisested|food|café?|diner|pub|bar|fika|konditori|värdshus|handelsbod|servering|gatukök/i.test(txt)) return 'food';

  // Attraction / POI (NO: kirke/museum/stavkirke, SE: kyrka/slott)
  if (/kirke[rn]?|church|museum|sever|attrak|stav(?:kirke|kyrka)|historisk|monument|ruin(?:er)?|fyr(?:et|en|tårn)?|lighthouse|slott(?:et)?|castle|kyrk(?:a|an|or)|forn(?:lämning|minne)|grav(?:högar?|hauger?|felt)|herrgård|borg(?:en)?|kloster|gruv(?:a|an|or)|sag[ae]n?|mil(?:stolp|sten)|bauta/i.test(txt)) return 'star';

  return 'pin';
};

export const classifyWaypoints = async (
  waypoints: { name: string; description?: string }[]
): Promise<WaypointIcon[] | null> => {
  try {
    const response = await aiClassifyWaypoints(waypoints);
    if (response?.ok && Array.isArray(response?.data?.icons)) {
      return response.data.icons.map(icon => {
        const s = String(icon || 'pin').toLowerCase() as WaypointIcon;
        return VALID_ICONS.includes(s) ? s : 'pin';
      });
    }
    // AI unavailable — use keyword-based fallback
    console.warn('[classifyWaypoints] AI utilgjengelig, bruker heuristisk fallback');
    return waypoints.map(wp => heuristicWaypointIcon(wp.name, wp.description));
  } catch {
    console.warn('[classifyWaypoints] Feil, bruker heuristisk fallback');
    return waypoints.map(wp => heuristicWaypointIcon(wp.name, wp.description));
  }
};

const VALID_CATEGORIES: VehicleCategory[] = ['motorhome', 'campervan', 'caravan', 'camper_trailer', 'rooftent', 'offroad'];
const VALID_TERRAIN: TerrainPreference[] = ['paved', 'gravel', 'forest', 'sand', 'snow', 'mountain', 'coastal'];

export interface RouteSuitability {
  suitableCategories: VehicleCategory[];
  terrainTypes: TerrainPreference[];
  requires4wd: boolean;
  maxRecommendedLengthCm?: number;
  suitabilityNote?: string;
}

/**
 * Heuristic fallback when AI is unavailable (quota exhausted, network error, etc.)
 * Classifies based on elevation, distance, name keywords, and road surface data.
 */
const heuristicSuitability = (routeData: {
  name?: string; description?: string; distanceKm?: number; elevationGain?: number;
  maxElevation?: number; roadSurfaces?: Record<string, number>; roadHighways?: Record<string, number>;
}): RouteSuitability => {
  const { name = '', description = '', distanceKm = 0, elevationGain = 0, maxElevation = 0, roadSurfaces, roadHighways } = routeData;
  const text = `${name} ${description}`.toLowerCase();

  // Determine terrain from road surfaces or keywords
  const terrainTypes: TerrainPreference[] = [];
  const categories: VehicleCategory[] = [];
  let requires4wd = false;
  let maxLen = 900; // generous default

  // Use OSM road surface data if available
  if (roadSurfaces && Object.keys(roadSurfaces).length > 0) {
    const total = Object.values(roadSurfaces).reduce((s, v) => s + v, 0);
    const pct = (key: string) => ((roadSurfaces[key] || 0) / total) * 100;
    const hasSurface = (keys: string[]) => keys.some(k => roadSurfaces[k]);

    if (hasSurface(['asphalt', 'paved', 'concrete'])) terrainTypes.push('paved');
    if (hasSurface(['gravel', 'fine_gravel', 'compacted'])) terrainTypes.push('gravel');
    if (hasSurface(['dirt', 'earth', 'ground', 'mud'])) { terrainTypes.push('forest'); requires4wd = true; }
    if (hasSurface(['sand'])) { terrainTypes.push('sand'); requires4wd = true; }

    const unpavedPct = 100 - (pct('asphalt') + pct('paved') + pct('concrete'));
    if (unpavedPct > 50) { maxLen = 700; }
  }

  // Use highway classes if available
  if (roadHighways && Object.keys(roadHighways).length > 0) {
    const hasHwy = (keys: string[]) => keys.some(k => roadHighways[k]);
    if (hasHwy(['track', 'path'])) { if (!terrainTypes.includes('forest')) terrainTypes.push('forest'); requires4wd = true; maxLen = 600; }
  }

  // Keyword-based terrain detection
  if (text.match(/fjell|mountain|pass|høyfjell/)) { if (!terrainTypes.includes('mountain')) terrainTypes.push('mountain'); }
  if (text.match(/kyst|coast|sjø|hav/)) { if (!terrainTypes.includes('coastal')) terrainTypes.push('coastal'); }
  if (text.match(/skog|forest|woods/)) { if (!terrainTypes.includes('forest')) terrainTypes.push('forest'); }
  if (text.match(/grus|gravel/)) { if (!terrainTypes.includes('gravel')) terrainTypes.push('gravel'); }

  // Default terrain if nothing detected
  if (terrainTypes.length === 0) terrainTypes.push('paved');

  // Elevation-based difficulty
  const isHilly = elevationGain > 500 || maxElevation > 800;
  const isMountain = elevationGain > 1000 || maxElevation > 1200;

  if (isMountain && !terrainTypes.includes('mountain')) terrainTypes.push('mountain');

  // Vehicle suitability based on terrain + elevation
  categories.push('rooftent', 'campervan'); // almost always suitable
  if (requires4wd || isMountain || terrainTypes.includes('forest') || terrainTypes.includes('sand')) {
    categories.push('offroad');
  }
  if (!requires4wd && !isMountain) {
    categories.push('motorhome');
    if (!isHilly) { categories.push('caravan', 'camper_trailer'); }
    else { categories.push('camper_trailer'); maxLen = Math.min(maxLen, 800); }
  } else if (!requires4wd) {
    categories.push('motorhome', 'camper_trailer');
    maxLen = Math.min(maxLen, 750);
  }

  // Build note
  let note = 'Heuristisk vurdering (AI utilgjengelig)';
  if (requires4wd) note = '4WD anbefalt — heuristisk vurdering';
  else if (isMountain) note = 'Krevende fjellrute — heuristisk vurdering';

  return {
    suitableCategories: categories,
    terrainTypes,
    requires4wd,
    maxRecommendedLengthCm: maxLen > 0 ? Math.round(maxLen) : undefined,
    suitabilityNote: note,
  };
};

export const classifyRouteSuitability = async (routeData: {
  name: string; description: string; distanceKm?: number; elevationGain?: number;
  maxElevation?: number; creatorVehicleCategory?: string; creatorVehicleLength?: number;
  creatorVehicleIs4wd?: boolean; region?: string;
  roadSurfaces?: Record<string, number>; roadHighways?: Record<string, number>;
}): Promise<RouteSuitability | null> => {
  try {
    const response = await aiClassifyRouteSuitability(routeData);
    if (response?.ok && response?.data) {
      const d = response.data;
      return {
        suitableCategories: (d.suitableCategories || []).filter(c => VALID_CATEGORIES.includes(c as VehicleCategory)) as VehicleCategory[],
        terrainTypes: (d.terrainTypes || []).filter(t => VALID_TERRAIN.includes(t as TerrainPreference)) as TerrainPreference[],
        requires4wd: !!d.requires4wd,
        maxRecommendedLengthCm: d.maxRecommendedLengthCm || undefined,
        suitabilityNote: d.suitabilityNote || undefined,
      };
    }
    // AI unavailable — use heuristic fallback
    console.warn('[classifyRouteSuitability] AI utilgjengelig (reason:', response?.reason || 'unknown', '), bruker heuristisk fallback');
    return heuristicSuitability(routeData);
  } catch (e) {
    console.warn('[classifyRouteSuitability] Feil, bruker heuristisk fallback:', e);
    return heuristicSuitability(routeData);
  }
};

